import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Pagination,
  PaginationItem,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";

import type { SiteListing } from "@/api/zero/sites";

import { useAccounts } from "@/api/zero/accounts";
import { zeroSiteKeys, updateSite, useSites } from "@/api/zero/sites";
import {
  AudienceTableContainer,
  HeaderCell,
  RowCell,
} from "@/features/Audiences/AudienceTable";
import { alternatingRowBg } from "@/features/Audiences/tableUtils";
import { SettingsSection } from "@/features/Settings/components/SettingsSection";
import { SettingsPageLayout } from "@/features/Settings/SettingsPageLayout";
import {
  AccountStatusChip,
  FlagCell,
} from "@/features/ZeroAdmin/components/StatusChip";
import {
  TableMessageRow,
  TableSkeletonRows,
} from "@/features/ZeroAdmin/components/TableStateRows";
import {
  filterSiteListings,
  sortSiteListings,
  type SiteSortField,
} from "@/features/ZeroAdmin/sites/service";
import {
  FirstPageIcon,
  KeyboardArrowLeftIcon,
  KeyboardArrowRightIcon,
  LastPageIcon,
  SearchIcon,
} from "@/icons";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const sortFieldSchema = z.enum(["url", "brand", "account"]);

const searchSchema = z.object({
  q: z.string().optional(),
  account: z.number().optional(),
  includeDeleted: z.boolean().optional(),
  isConnect: z.boolean().optional(),
  isAnalog: z.boolean().optional(),
  sort: sortFieldSchema.optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  page: z.number().int().positive().optional(),
});

export const Route = createFileRoute(
  "/_auth/settings/$siteId/_zeroAdmin/sites/",
)({
  validateSearch: zodValidator(searchSchema),
  component: SitesListPage,
});

const TABLE_COLUMN_COUNT = 7;
const PAGE_SIZE = 50;

const deletedDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

function formatDeletedDate(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "Deleted"
    : `Deleted ${deletedDateFormatter.format(parsed)}`;
}

const boolToFilterValue = (value: boolean | undefined) =>
  value === undefined ? "" : value ? "true" : "false";

const filterValueToBool = (value: string): boolean | undefined =>
  value === "" ? undefined : value === "true";

function SitesListPage() {
  const search = Route.useSearch();
  const { siteId } = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: sites,
    isLoading,
    isError,
  } = useSites({
    includeDeleted: search.includeDeleted,
  });

  const { data: accounts } = useAccounts();

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const accountNameById = useMemo(() => {
    const map = new Map<number, string>();
    (accounts ?? []).forEach((a) => map.set(Number(a.id), a.name));
    return map;
  }, [accounts]);

  const accountLabel = useCallback(
    (id: number) => accountNameById.get(id) ?? `#${id}`,
    [accountNameById],
  );

  const accountOptions = useMemo(() => {
    const ids = new Set<number>();
    (sites ?? []).forEach((s) => ids.add(s.account_id));
    return Array.from(ids)
      .map((id) => ({ id, label: accountNameById.get(id) ?? `#${id}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sites, accountNameById]);

  const selectedAccount = useMemo(
    () => accountOptions.find((o) => o.id === search.account) ?? null,
    [accountOptions, search.account],
  );

  const sorted = useMemo(() => {
    const filtered = filterSiteListings(
      sites ?? [],
      {
        q: search.q,
        account: search.account,
        isConnect: search.isConnect,
        isAnalog: search.isAnalog,
      },
      accountLabel,
    );
    return sortSiteListings(filtered, search.sort, search.dir, accountLabel);
  }, [
    sites,
    search.q,
    search.account,
    search.isConnect,
    search.isAnalog,
    search.sort,
    search.dir,
    accountLabel,
  ]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const page = Math.min(Math.max(search.page ?? 1, 1), totalPages);
  const pageStart = (page - 1) * PAGE_SIZE;
  const paged = useMemo(
    () => sorted.slice(pageStart, pageStart + PAGE_SIZE),
    [sorted, pageStart],
  );

  const setTracking = useMutation({
    mutationFn: async (enabled: boolean) => {
      const rjIds = Array.from(selected);
      await Promise.all(
        rjIds.map((rjId) =>
          updateSite(rjId, { tracking_links_enabled: enabled }, [
            "tracking_links_enabled",
          ]),
        ),
      );
    },
    onSuccess: () => {
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: zeroSiteKeys.all() });
    },
    onError: (error) =>
      enqueueErrorSnackbar(error, "Failed to update link tracking"),
  });

  const toggleRow = (rjId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rjId)) next.delete(rjId);
      else next.add(rjId);
      return next;
    });

  const allVisibleSelected =
    paged.length > 0 && paged.every((s) => selected.has(s.rj_id));
  const someVisibleSelected = paged.some((s) => selected.has(s.rj_id));

  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) paged.forEach((s) => next.delete(s.rj_id));
      else paged.forEach((s) => next.add(s.rj_id));
      return next;
    });

  const toggleSort = (field: SiteSortField) =>
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        sort: field,
        dir: prev.sort === field && prev.dir === "asc" ? "desc" : "asc",
        page: undefined,
      }),
    });

  const sortDirection = (field: SiteSortField) =>
    search.sort === field ? (search.dir ?? "asc") : "asc";

  const goToSite = (listing: SiteListing) =>
    void navigate({
      to: "/settings/$siteId/sites/$rjId",
      params: { siteId: siteId ?? "", rjId: listing.rj_id },
    });

  const totalCount = sites?.length ?? 0;
  const noun = totalCount === 1 ? "site" : "sites";
  const resultLabel = isLoading
    ? "Loading sites…"
    : sorted.length === totalCount
      ? `${totalCount} ${noun}`
      : `${sorted.length} of ${totalCount} ${noun}`;

  return (
    <SettingsPageLayout
      breadcrumbs={[{ label: "Admin" }]}
      title="Sites"
      description="Every site across all accounts. Select rows for bulk actions."
      data-testid="sites-list-page"
    >
      <SettingsSection
        title="All sites"
        description={resultLabel}
        data-testid="sites-list-section"
        actions={
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search URL, brand, account, or hashID"
            value={search.q ?? ""}
            onChange={(e) =>
              void navigate({
                to: ".",
                search: (prev) => ({
                  ...prev,
                  q: e.target.value || undefined,
                  page: undefined,
                }),
              })
            }
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 2 }} fontSize="small" />,
              },
              htmlInput: { "data-testid": "sites-search-input" },
            }}
            sx={{ width: 320 }}
          />
        }
      >
        <Stack sx={{ gap: 3 }}>
          <Stack
            direction="row"
            sx={{ gap: 2, alignItems: "center", flexWrap: "wrap" }}
          >
            <Autocomplete
              size="small"
              options={accountOptions}
              value={selectedAccount}
              // Account names are not unique (staging has 37 "My Store"s), and
              // MUI keys options by their label unless told otherwise —
              // duplicate keys left React rendering stale rows, so typing
              // looked like it filtered to the wrong accounts.
              getOptionKey={(option) => option.id}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              onChange={(_, newValue) =>
                void navigate({
                  to: ".",
                  search: (prev) => ({
                    ...prev,
                    account: newValue?.id,
                    page: undefined,
                  }),
                })
              }
              data-testid="sites-account-filter"
              sx={{ minWidth: 220 }}
              renderInput={(params) => (
                <TextField {...params} label="Account" placeholder="Search…" />
              )}
            />
            <TextField
              size="small"
              select
              label="Connect"
              value={boolToFilterValue(search.isConnect)}
              onChange={(e) =>
                void navigate({
                  to: ".",
                  search: (prev) => ({
                    ...prev,
                    isConnect: filterValueToBool(e.target.value),
                    page: undefined,
                  }),
                })
              }
              data-testid="sites-connect-filter"
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Connect</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
            <TextField
              size="small"
              select
              label="Analog"
              value={boolToFilterValue(search.isAnalog)}
              onChange={(e) =>
                void navigate({
                  to: ".",
                  search: (prev) => ({
                    ...prev,
                    isAnalog: filterValueToBool(e.target.value),
                    page: undefined,
                  }),
                })
              }
              data-testid="sites-analog-filter"
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Analog</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={search.includeDeleted ?? false}
                  onChange={(e) =>
                    void navigate({
                      to: ".",
                      search: (prev) => ({
                        ...prev,
                        includeDeleted: e.target.checked || undefined,
                        page: undefined,
                      }),
                    })
                  }
                  data-testid="sites-include-deleted"
                />
              }
              label="Include deleted"
            />
            <Box sx={{ flexGrow: 1 }} />
            {selected.size > 0 && (
              <Stack
                direction="row"
                sx={{ gap: 2, alignItems: "center" }}
                data-testid="sites-bulk-toolbar"
              >
                <Typography variant="body2">
                  {selected.size} selected
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={setTracking.isPending}
                  onClick={() => setTracking.mutate(true)}
                  data-testid="sites-bulk-enable-tracking"
                >
                  Enable link tracking
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={setTracking.isPending}
                  onClick={() => setTracking.mutate(false)}
                  data-testid="sites-bulk-disable-tracking"
                >
                  Disable link tracking
                </Button>
              </Stack>
            )}
          </Stack>

          <AudienceTableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <HeaderCell
                    size="small"
                    padding="checkbox"
                    sx={{ "&&": { width: 56, px: "8px" } }}
                  >
                    <Checkbox
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected && !allVisibleSelected}
                      onChange={toggleSelectAll}
                      data-testid="sites-select-all"
                    />
                  </HeaderCell>
                  <HeaderCell size="small">
                    <TableSortLabel
                      active={search.sort === "url"}
                      direction={sortDirection("url")}
                      onClick={() => toggleSort("url")}
                      data-testid="sites-sort-url"
                    >
                      URL
                    </TableSortLabel>
                  </HeaderCell>
                  <HeaderCell size="small" sx={{ width: 220 }}>
                    <TableSortLabel
                      active={search.sort === "brand"}
                      direction={sortDirection("brand")}
                      onClick={() => toggleSort("brand")}
                      data-testid="sites-sort-brand"
                    >
                      Brand
                    </TableSortLabel>
                  </HeaderCell>
                  <HeaderCell size="small" sx={{ width: 220 }}>
                    <TableSortLabel
                      active={search.sort === "account"}
                      direction={sortDirection("account")}
                      onClick={() => toggleSort("account")}
                      data-testid="sites-sort-account"
                    >
                      Account
                    </TableSortLabel>
                  </HeaderCell>
                  <HeaderCell size="small" sx={{ width: 150 }}>
                    Account status
                  </HeaderCell>
                  <HeaderCell size="small" sx={{ width: 110 }}>
                    Connect
                  </HeaderCell>
                  <HeaderCell size="small" sx={{ width: 110 }}>
                    Analog
                  </HeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableSkeletonRows columns={TABLE_COLUMN_COUNT} />
                ) : isError ? (
                  <TableMessageRow
                    colSpan={TABLE_COLUMN_COUNT}
                    tone="error"
                    data-testid="sites-list-error"
                  >
                    Failed to load sites.
                  </TableMessageRow>
                ) : sorted.length === 0 ? (
                  <TableMessageRow
                    colSpan={TABLE_COLUMN_COUNT}
                    data-testid="sites-list-empty"
                  >
                    No sites match these filters.
                  </TableMessageRow>
                ) : (
                  paged.map((site, index) => {
                    const isDeleted = Boolean(site.deleted_at);
                    return (
                      <TableRow
                        key={site.rj_id}
                        data-testid={`sites-row-${site.rj_id}`}
                        data-deleted={isDeleted || undefined}
                        hover
                        sx={{
                          backgroundColor: alternatingRowBg(index),
                          ...(isDeleted ? { opacity: 0.55 } : {}),
                        }}
                      >
                        <RowCell
                          size="small"
                          padding="checkbox"
                          sx={{ "&&": { width: 56, px: "8px" } }}
                        >
                          <Checkbox
                            checked={selected.has(site.rj_id)}
                            onChange={() => toggleRow(site.rj_id)}
                            data-testid={`sites-row-checkbox-${site.rj_id}`}
                          />
                        </RowCell>
                        <RowCell
                          size="small"
                          sx={{ cursor: "pointer" }}
                          onClick={() => goToSite(site)}
                        >
                          <Stack
                            direction="row"
                            sx={{ gap: 2, alignItems: "center" }}
                          >
                            <Typography variant="body2">{site.url}</Typography>
                            {isDeleted && site.deleted_at ? (
                              <Chip
                                size="small"
                                variant="lightGray"
                                label={formatDeletedDate(site.deleted_at)}
                                data-testid={`sites-row-deleted-${site.rj_id}`}
                              />
                            ) : null}
                          </Stack>
                        </RowCell>
                        <RowCell
                          size="small"
                          sx={{ cursor: "pointer" }}
                          onClick={() => goToSite(site)}
                        >
                          {site.brand_name || "—"}
                        </RowCell>
                        <RowCell size="small">
                          {accountLabel(site.account_id)}
                        </RowCell>
                        <RowCell size="small">
                          {site.account ? (
                            <AccountStatusChip status={site.account.status} />
                          ) : (
                            "—"
                          )}
                        </RowCell>
                        <RowCell size="small">
                          <FlagCell
                            value={site.is_connect}
                            trueLabel="Connect"
                          />
                        </RowCell>
                        <RowCell size="small">
                          <FlagCell
                            value={site.is_analog_sequence_enabled}
                            trueLabel="Analog"
                          />
                        </RowCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </AudienceTableContainer>

          {sorted.length > PAGE_SIZE && (
            <Stack
              direction="row"
              sx={{ gap: 2, alignItems: "center", flexWrap: "wrap" }}
              data-testid="sites-pagination"
            >
              <Typography variant="body2" color="text.secondary">
                Showing {pageStart + 1}–{pageStart + paged.length} of{" "}
                {sorted.length}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Pagination
                page={page}
                count={totalPages}
                showFirstButton
                showLastButton
                onChange={(_event, nextPage) =>
                  void navigate({
                    to: ".",
                    search: (prev) => ({
                      ...prev,
                      page: nextPage === 1 ? undefined : nextPage,
                    }),
                    replace: true,
                  })
                }
                renderItem={(item) => (
                  <PaginationItem
                    slots={{
                      first: FirstPageIcon,
                      last: LastPageIcon,
                      previous: KeyboardArrowLeftIcon,
                      next: KeyboardArrowRightIcon,
                    }}
                    {...item}
                  />
                )}
              />
            </Stack>
          )}
        </Stack>
      </SettingsSection>
    </SettingsPageLayout>
  );
}
